import * as React from "react";
import { cn } from "@/lib/utils";

// Switch — small accessible toggle. Native pattern is a `button` with
// `role="switch"` + `aria-checked`; we deliberately don't reach for a Radix
// primitive here since the surface is one knob and the install cost isn't
// worth it for one component. Keyboard: Space / Enter toggles, Tab focuses.
//
// Controlled-only: callers pass `checked` and `onCheckedChange`. There's no
// uncontrolled variant — every consumer in this codebase reads from server
// state and would have to reconcile the two anyway.
export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, disabled, className, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          "inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-transparent",
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked ? "bg-brand-violet" : "bg-muted",
          className,
        )}
        {...rest}
      >
        <span
          aria-hidden
          className={cn(
            "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
            checked ? "translate-x-5" : "translate-x-0.5",
          )}
        />
      </button>
    );
  },
);
Switch.displayName = "Switch";
